import store from '@/store';
import {isPWA} from '@/utils/isPWA';

// Backend endpoint to receive error logs
const urlBase = `${import.meta.env.VITE_APP_API}/v2`;
const ERROR_LOG_ENDPOINT = `${urlBase}/logs`;

export const sendLogs = (params) => {
  let basic = {
    level: 'info',
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    userData: userDetails(),
    isPWA: isPWA(),
  };
  sendErrorToBackend(JSON.stringify({...basic, ...params}));
};
/**
 * Send error details to the backend.
 * @param {Object} errorDetails - Error information.
 */
export const sendErrorToBackend = (errorDetails) => {
  if (import.meta.env.VITE_APP_ENV !== 'live') {
    return;
  }
  const payload = {
    company_id: localStorage.getItem('FYGR_COMPANY'),
    message: errorDetails || 'An unknown error occurred',
  };

  fetch(ERROR_LOG_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('FYGR_ACCESS_TOKEN')}`,
    },
    body: JSON.stringify(payload), // Send as a string
  })
    .then((response) => {
      if (!response.ok) {
        console.error('Error sending to backend:', response.statusText);
      }
    })
    .catch((err) => {
      console.error('Failed to send error log to backend:', err);
    });
};

export const userDetails = () => {
  return {
    companyId: localStorage.getItem('FYGR_COMPANY'),
    lastModifiedIndexFile: localStorage.getItem('FYGR_LAST_MODIFIED'),
    locale: localStorage.getItem('LOCALE'),
    permissions: store.state.user?.permissions,
    isCompanyCpa: store.state.user?.isCompanyCpa,
    userObj: store.getters['user/userObj'],
    pretendedUser: store.getters['user/pretendedUser'],
    pretendedCompany: store.state?.user?.pretendedCompany,
  };
};

// Handle window.onerror (Global JS Errors)
window.onerror = (message, source, lineno, colno, error) => {
  const errorDetails = {
    type: 'window.onerror',
    level: 'error',
    message,
    source,
    line: lineno,
    url: window.location.href,
    column: colno,
    stack: error?.stack || null,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    userData: userDetails(),
    isPWA: isPWA(),
  };
  console.error('Global Error Caught:', errorDetails);
  const errorDetailsString = JSON.stringify(errorDetails);
  sendErrorToBackend(errorDetailsString);
};

// Handle window.unhandledrejection (Promise Errors)
window.addEventListener('unhandledrejection', (event) => {
  const errorDetails = {
    type: 'unhandledrejection',
    level: 'error',
    url: window.location.href,
    message: event.reason?.message || 'Unhandled promise rejection',
    stack: event.reason?.stack || null,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    userData: userDetails(),
    isPWA: isPWA(),
    extraData: event?.reason?.extraData,
  };
  console.error('Unhandled Rejection Caught:', errorDetails);
  sendErrorToBackend(errorDetails);
});
// Export Vue 3 Error Handler
export const vueErrorHandler = (err, instance, info) => {
  console.error('Vue caught an error:', err);
  const componentName =
    instance?.type?.name ||
    instance?.type?.__file ||
    instance?.vnode?.tag ||
    instance?.proxy?.$options?.name ||
    instance?.proxy?.$options?.__file ||
    instance?.vnode?.type?.name ||
    instance?.vnode?.type?.__file ||
    'Anonymous Component';

  const route = instance?.proxy?.$route;
  const routeName = route?.name || route?.path || 'Unknown Route';

  const parentComponent = instance?.parent?.type?.name || 'No Parent';
  const errorDetails = {
    type: 'vue.errorHandler',
    level: 'error',
    message: err.message || 'Vue error',
    stack: err.stack || null,
    info,
    url: window.location.href,
    component: componentName,
    route: routeName,
    parentComponent,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    userData: userDetails(),
    isPWA: isPWA(),
    extraData: err?.extraData,
  };
  if (!errorDetails?.stack && errorDetails.message === 'Vue error') {
    return;
  }
  const errorDetailsString = JSON.stringify(errorDetails);
  sendErrorToBackend(errorDetailsString);
};
